import { FormControl, Switch } from '@chakra-ui/react';
import { useController } from 'react-hook-form';
import FormFieldMeta from './FormFieldMeta';
import type { ContainerProps, StyleProps, SwitchProps } from '@chakra-ui/react';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';

type SwitchFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<SwitchProps, 'onChange' | 'isChecked'> & {
  control?: Control<TFieldValues>;
  name: TName;
  isRequired?: boolean;
  label?: string;
  helperText?: string;
  containerProps?: StyleProps;
  onChange?: (value: boolean) => void;
  showHelperContainer?: boolean;
  helperTextStyles?: ContainerProps;
};

const SwitchField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  control,
  isRequired = false,
  helperText,
  label,
  containerProps,
  onChange,
  helperTextStyles,
  ...rest
}: SwitchFieldProps<TFieldValues, TName>) => {
  const {
    fieldState,
    field: { value, onChange: onFieldChange, ...fieldProps },
  } = useController({ name, control });

  const { invalid, error } = fieldState;

  const handleChange: SwitchProps['onChange'] = (e) => {
    onFieldChange(e.target.checked);
    onChange?.(e.target.checked);
  };

  return (
    <FormControl
      isInvalid={invalid}
      isRequired={isRequired}
      w='full'
      {...containerProps}
    >
      <Switch
        alignItems='center'
        display='flex'
        isChecked={value}
        lineHeight='normal'
        mt='0'
        onChange={handleChange}
        {...fieldProps}
        {...rest}
      >
        {label}
      </Switch>
      <FormFieldMeta
        errors={{ [name]: error }}
        helperText={helperText}
        helperTextStyles={helperTextStyles}
        name={name}
      />
    </FormControl>
  );
};

export default SwitchField;
